import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  LayoutDashboard,
  FileText,
  Users,
  CircleDollarSign,
  Phone,
  Building2,
  UserRound,
  ChartNoAxesColumn,
  ListTodo,
  Settings as SettingsIcon,
  ShieldCheck,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import { supabase } from './supabase'
import { ToastProvider } from './Toast'
import { UserProvider, useUser } from './UserContext'
import OrgGuard from './OrgGuard'
import Clients from './pages/Clients'
import ClientDetail from './pages/ClientDetail'
import Contracts from './pages/Contracts'
import PortfolioDetail from './pages/PortfolioDetail'
import ProjectDetail from './pages/ProjectDetail'
import Deals from './pages/Deals'
import Rights from './pages/Rights'
import Deliveries from './pages/Deliveries'
import Dashboard from './pages/Dashboard'
import DealDetail from './pages/DealDetail'
import Auth from './pages/Auth'
import Team from './pages/Team'
import Settings from './pages/Settings'
import Profile from './pages/Profile'
import Leads from './pages/Leads'
import Companies from './pages/Companies'
import Contacts from './pages/Contacts'
import ContactDetail from './pages/ContactDetail'
import CompanyDetail from './pages/CompanyDetail'
import Pipeline from './pages/Pipeline'
import OpportunityDetail from './pages/OpportunityDetail'
import Activities from './pages/Activities'
import Tasks from './pages/Tasks'
import SystemAdmin from './pages/SystemAdmin'
import PlatformTodos from './pages/PlatformTodos'
import AllocationDetail from './pages/AllocationDetail'
import './app.css'
import Calendar from './pages/Calendar'
import PermissionSets from './pages/PermissionSets'
import Alerts from './pages/Alerts'
import CrmDashboard from './pages/CrmDashboard'


const iconProps = {
  size: 18,
  strokeWidth: 1.75
}

const CORE_NAV = [
  { path: '/', label: 'Dashboard', end: true, icon: <LayoutDashboard {...iconProps} /> },
  { path: '/contracts', label: 'Projects', icon: <FileText {...iconProps} /> },
  { path: '/clients', label: 'Clients', icon: <Users {...iconProps} /> },
  { path: '/deals', label: 'Deals', icon: <CircleDollarSign {...iconProps} /> },
]

const AGENCY_NAV = [
  { path: '/crm', label: 'CRM Dashboard', icon: <LayoutDashboard {...iconProps} /> },
  { path: '/leads', label: 'Leads', icon: <Phone {...iconProps} /> },
  { path: '/companies', label: 'Companies', icon: <Building2 {...iconProps} /> },
  { path: '/contacts', label: 'Contacts', icon: <UserRound {...iconProps} /> },
  { path: '/pipeline', label: 'Pipeline', icon: <ChartNoAxesColumn {...iconProps} /> },
]

const BOTTOM_NAV = [
  { path: '/team', label: 'Team', icon: <Users {...iconProps} /> },
  { path: '/settings', label: 'Settings', icon: <SettingsIcon {...iconProps} /> },
]

const SYSTEM_NAV = [
  { path: '/system-admin', label: 'System Admin', icon: <ShieldCheck {...iconProps} /> },
  { path: '/platform-todos', label: 'To-do List', icon: <ListTodo {...iconProps} /> },
]

function getInitials(name, email) {
  const source = name?.trim() || email || ''
  if (!source) return '?'

  const localPart = source.includes('@') ? source.split('@')[0] : source
  const parts = localPart.split(/[\s._-]+/).filter(Boolean)

  return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('')
}

function NavItem({ item }) {
  return (
    <NavLink
      to={item.path}
      end={item.end}
      className={({ isActive }) => `hv-nav-item${isActive ? ' hv-nav-item--active' : ''}`}
    >
      <span className="hv-nav-icon">{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  )
}

function PipelineNav({ workspaceId }) {
  const location = useLocation()
  const onPipelineRoute = location.pathname.startsWith('/pipeline')
  const [expanded, setExpanded] = useState(onPipelineRoute)
  const [businesses, setBusinesses] = useState([])

  useEffect(() => {
    if (!workspaceId) {
      setBusinesses([])
      return
    }

    let active = true
    supabase.from('crm_businesses').select('id, name').eq('workspace_id', workspaceId).eq('archived', false).order('name').then(({ data }) => {
      if (active) setBusinesses(data || [])
    })
    return () => { active = false }
  }, [workspaceId])

  useEffect(() => {
    if (onPipelineRoute) setExpanded(true)
  }, [onPipelineRoute])

  const requestedBusinessId = new URLSearchParams(location.search).get('business')
  const storedBusinessId = workspaceId ? window.localStorage.getItem(`delivr.pipeline-business.${workspaceId}`) : null
  const selectedBusinessId = businesses.some(item => item.id === requestedBusinessId)
    ? requestedBusinessId
    : businesses.some(item => item.id === storedBusinessId) ? storedBusinessId : businesses[0]?.id
  const pipelinePath = selectedBusinessId ? `/pipeline?business=${encodeURIComponent(selectedBusinessId)}` : '/pipeline'

  return (
    <div className="hv-nav-group">
      <div className="hv-nav-parent">
        <NavLink to={pipelinePath} className={() => `hv-nav-item${onPipelineRoute ? ' hv-nav-item--active' : ''}`} onClick={() => setExpanded(true)}>
          <span className="hv-nav-icon"><ChartNoAxesColumn {...iconProps} /></span>
          <span>Pipeline</span>
        </NavLink>
        <button type="button" className="hv-nav-expand" aria-label={`${expanded ? 'Collapse' : 'Expand'} pipelines`} aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>
      {expanded && businesses.length > 0 && <div className="hv-nav-children">{businesses.map(business => (
        <NavLink key={business.id} to={`/pipeline?business=${encodeURIComponent(business.id)}`} className={() => `hv-nav-child${onPipelineRoute && selectedBusinessId === business.id ? ' hv-nav-child--active' : ''}`}>
          <span />{business.name}
        </NavLink>
      ))}</div>}
    </div>
  )
}

function AppShell({ session, onSignOut }) {
  const {
    profile,
    isAgency,
    isPlatformAdmin,
    can,
    crmWorkspaces,
    activeCrmWorkspaceId,
    setActiveCrmWorkspaceId,
    businessUnits,
    activeBusinessUnitId,
    setActiveBusinessUnitId,
    isAdmin
  } = useUser()
  const displayName = profile?.full_name?.trim() || session.user.email
  const canViewCrm = isAgency && can('crm.view')

  return (
    <div className="hv-shell">
      <aside className="hv-sidebar">
<NavLink
  to="/"
  end
  className="hv-logo"
  style={{
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center'
  }}
>
  <img
    src="/logo-delivr-sidebar.svg"
    alt="Delivr"
    className="hv-sidebar-logo"
    style={{
      width: '140px',
      maxHeight: '42px',
      objectFit: 'contain',
      display: 'block'
    }}
  />
</NavLink>

        {canViewCrm && crmWorkspaces.length > 1 && (
          <div className="hv-workspace-switcher">
            <label htmlFor="crm-workspace-select">CRM workspace</label>
            <select
              id="crm-workspace-select"
              value={activeCrmWorkspaceId || ''}
              onChange={event => setActiveCrmWorkspaceId(event.target.value)}
            >
              {crmWorkspaces.map(workspace => (
                <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
              ))}
            </select>
          </div>
        )}

      {businessUnits.length > 1 && (
          <div className="hv-workspace-switcher">
            <label htmlFor="business-unit-select">Business</label>
            <select
              id="business-unit-select"
              value={activeBusinessUnitId || ''}
              onChange={event => setActiveBusinessUnitId(event.target.value || null)}
            >
              {(isAdmin || isPlatformAdmin) && <option value="">All Businesses</option>}
              {businessUnits.map(unit => (
                <option key={unit.id} value={unit.id}>{unit.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="hv-sidebar-scroll">
          <div className="hv-nav-label">Core</div>
          <nav className="hv-nav">
            {CORE_NAV.map(item => <NavItem key={item.path} item={item} />)}
          </nav>

          {canViewCrm && (
            <>
              <div className="hv-nav-label" style={{ marginTop: '12px' }}>CRM</div>
              <nav className="hv-nav">
                {AGENCY_NAV.map(item => item.path === '/pipeline'
                  ? <PipelineNav key={item.path} workspaceId={activeCrmWorkspaceId} />
                  : <NavItem key={item.path} item={item} />)}
              </nav>
            </>
          )}

          <div className="hv-nav-label" style={{ marginTop: '12px' }}>Workspace</div>
          <nav className="hv-nav">
            {BOTTOM_NAV.map(item => <NavItem key={item.path} item={item} />)}
            {can('permissions.manage') && (
              <NavItem item={{ path: '/permissions', label: 'Permission Sets', icon: <ShieldCheck {...iconProps} /> }} />
            )}
          </nav>

          {isPlatformAdmin && (
            <>
              <div className="hv-nav-label" style={{ marginTop: '12px' }}>Platform</div>
              <nav className="hv-nav">
                {SYSTEM_NAV.map(item => <NavItem key={item.path} item={item} />)}
              </nav>
            </>
          )}
        </div>

        <div className="hv-user">
          <div className="hv-user-avatar">{getInitials(profile?.full_name, session.user.email)}</div>
          <div className="hv-user-info">
            <NavLink to="/profile" className="hv-user-email" title={displayName}>{displayName}</NavLink>
            <button className="hv-signout" onClick={onSignOut}>Sign out</button>
          </div>
        </div>
      </aside>

      <main className="hv-main">
        <OrgGuard>
          <Routes>
            <Route path='/' element={<Dashboard />} />
            <Route path='/clients' element={<Clients />} />
            <Route path='/clients/:id' element={<ClientDetail />} />
            <Route path='/contracts' element={<Contracts />} />
            <Route path='/portfolios/:id' element={<PortfolioDetail />} />
            <Route path='/projects/:id' element={<ProjectDetail />} />
            <Route path='/allocations/:id' element={<AllocationDetail />} />
            <Route path='/deals' element={<Deals />} />
            <Route path='/deals/:id' element={<DealDetail />} />
            <Route path='/rights' element={<Rights />} />
            <Route path='/deliveries' element={<Deliveries />} />
            <Route path='/alerts' element={<Alerts />} />
            <Route path='/team' element={<Team />} />
            <Route path='/settings' element={<Settings />} />
            <Route path='/profile' element={<Profile />} />
            {can('permissions.manage') && <Route path='/permissions' element={<PermissionSets />} />}
            <Route path='/calendar' element={<Calendar />} />
            {canViewCrm && <Route path='/crm' element={<CrmDashboard />} />}
            {canViewCrm && <Route path='/leads' element={<Leads />} />}
            {canViewCrm && <Route path='/companies' element={<Companies />} />}
            {canViewCrm && <Route path='/companies/:id' element={<CompanyDetail />} />}
            {canViewCrm && <Route path='/contacts' element={<Contacts />} />}
            {canViewCrm && <Route path='/contacts/:id' element={<ContactDetail />} />}
            {canViewCrm && <Route path='/pipeline' element={<Pipeline />} />}
            {canViewCrm && <Route path='/pipeline/:id' element={<OpportunityDetail />} />}
            {canViewCrm && <Route path='/activities' element={<Activities />} />}
            {canViewCrm && <Route path='/tasks' element={<Tasks />} />}
            {isPlatformAdmin && <Route path='/system-admin' element={<SystemAdmin />} />}
            {isPlatformAdmin && <Route path='/platform-todos' element={<PlatformTodos />} />}
            <Route path='*' element={<Navigate to='/' />} />
          </Routes>
        </OrgGuard>
      </main>
    </div>
  )
}

function AuthedApp({ session, onSignOut }) {
  return (
    <UserProvider session={session}>
      <ToastProvider>
        <Router>
          <AppShell session={session} onSignOut={onSignOut} />
        </Router>
      </ToastProvider>
    </UserProvider>
  )
}

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  async function handleSignOut() {
    await supabase.auth.signOut()
  }

if (loading) return null

  if (!session) return <Auth />

  return <AuthedApp session={session} onSignOut={handleSignOut} />
}

export default App
